import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import {
    Thermometer,
    AlertTriangle,
    CheckCircle,
    TrendingUp,
    TrendingDown,
    Snowflake,
    Loader,
} from "lucide-react";

const IOTRefri = () => {
    const [stores, setStores] = useState([]);
    const [temperatures, setTemperatures] = useState({});
    const [loading, setLoading] = useState(true);

    // Cargar sucursales
    useEffect(() => {
        fetchStores();
        // Simular actualización de temperatura cada 5 segundos
        const interval = setInterval(() => {
            updateTemperatures();
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchStores = async () => {
        try {
            const storesSnapshot = await getDocs(collection(db, "stores"));
            const storesList = storesSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setStores(storesList);
            // Inicializar temperaturas
            initializeTemperatures(storesList);
        } catch (error) {
            console.error("Error fetching stores:", error);
        } finally {
            setLoading(false);
        }
    };

    const initializeTemperatures = (storesList) => {
        const temps = {};
        storesList.forEach((store) => {
            temps[store.id] = {
                current: getRandomTemp(0, 5),
                history: generateHistory(),
                trend: Math.random() > 0.5 ? "up" : "down",
                lastUpdate: new Date(),
            };
        });
        setTemperatures(temps);
    };

    const updateTemperatures = () => {
        setTemperatures((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((storeId) => {
                const temp = updated[storeId];
                // Simular variación pequeña
                const variation = (Math.random() - 0.5) * 0.5;
                let newTemp = temp.current + variation;

                // Mantener en rango realista
                newTemp = Math.max(-1, Math.min(7, newTemp));

                // Actualizar historial
                const newHistory = [...temp.history.slice(1), newTemp];

                updated[storeId] = {
                    current: parseFloat(newTemp.toFixed(1)),
                    history: newHistory,
                    trend: variation > 0 ? "up" : "down",
                    lastUpdate: new Date(),
                };
            });
            return updated;
        });
    };

    const getRandomTemp = (min, max) => {
        return parseFloat((Math.random() * (max - min) + min).toFixed(1));
    };

    const generateHistory = () => {
        return Array.from({ length: 20 }, () => getRandomTemp(0, 5));
    };

    const getTempStatus = (temp) => {
        if (temp < 0) return { status: "warning", label: "Muy Frío", color: "blue" };
        if (temp >= 0 && temp <= 4) return { status: "good", label: "Óptimo", color: "green" };
        if (temp > 4 && temp <= 6) return { status: "warning", label: "Alerta", color: "yellow" };
        return { status: "danger", label: "Peligro", color: "red" };
    };

    const MiniChart = ({ data, status }) => {
        const width = 120;
        const height = 40;
        const padding = 4;

        const max = Math.max(...data, 6);
        const min = Math.min(...data, 0);

        const points = data
            .map((value, index) => {
                const x = padding + (index * (width - padding * 2)) / (data.length - 1);
                const y = height - padding - ((value - min) / (max - min)) * (height - padding * 2);
                return `${x},${y}`;
            })
            .join(" ");

        const gradientId = `gradient-${Math.random().toString(36).substr(2, 9)}`;

        const colors = {
            green: { from: "#10b981", to: "#059669" },
            yellow: { from: "#f59e0b", to: "#d97706" },
            red: { from: "#ef4444", to: "#dc2626" },
            blue: { from: "#3b82f6", to: "#2563eb" },
        };

        const color = colors[status] || colors.green;

        return (
            <svg width={width} height={height} className="drop-shadow-sm">
                <defs>
                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={color.from} stopOpacity="0.6" />
                        <stop offset="100%" stopColor={color.to} stopOpacity="0.1" />
                    </linearGradient>
                </defs>

                {/* Area under curve */}
                <path
                    d={`M${padding},${height - padding} L${points} L${width - padding},${height - padding} Z`}
                    fill={`url(#${gradientId})`}
                />

                {/* Line */}
                <polyline
                    points={points}
                    fill="none"
                    stroke={color.from}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Last point dot */}
                <circle
                    cx={padding + ((data.length - 1) * (width - padding * 2)) / (data.length - 1)}
                    cy={height - padding - ((data[data.length - 1] - min) / (max - min)) * (height - padding * 2)}
                    r="3"
                    fill={color.from}
                    className="animate-pulse"
                />
            </svg>
        );
    };

    if (loading) {
        return (
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
                <Loader className="w-8 h-8 text-[#FF0000] animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Conectando con sensores IoT...</p>
            </div>
        );
    }

    if (stores.length === 0) {
        return (
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
                <Thermometer className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">No hay sucursales registradas</h3>
                <p className="text-gray-500 text-sm">Agrega sucursales para monitorear sus refrigeradores</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                                <Snowflake className="w-6 h-6" strokeWidth={2} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold">Sistema IoT - Refrigeración</h2>
                                <p className="text-blue-100 text-sm">Monitoreo en tiempo real</p>
                            </div>
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-4">
                        <div className="text-right">
                            <p className="text-blue-100 text-sm">Sucursales</p>
                            <p className="text-3xl font-bold">{stores.length}</p>
                        </div>
                        <div className="w-px h-12 bg-white/20"></div>
                        <div className="text-right">
                            <p className="text-blue-100 text-sm">Activos</p>
                            <p className="text-3xl font-bold text-green-300">
                                {Object.values(temperatures).filter(t => getTempStatus(t.current).status === "good").length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Temperature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stores.map((store) => {
                    const temp = temperatures[store.id];
                    if (!temp) return null;

                    const status = getTempStatus(temp.current);
                    const TrendIcon = temp.trend === "up" ? TrendingUp : TrendingDown;

                    return (
                        <div
                            key={store.id}
                            className="bg-white rounded-xl border-2 border-gray-100 hover:border-gray-200 transition-all hover:shadow-lg p-5"
                        >
                            {/* Store Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-gray-900 truncate">
                                        {store.storeName}
                                    </h3>
                                    <p className="text-xs text-gray-500 truncate">
                                        {store.address}
                                    </p>
                                </div>
                                <div
                                    className={`px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1 ${status.status === "good"
                                            ? "bg-green-100 text-green-700"
                                            : status.status === "warning"
                                                ? "bg-yellow-100 text-yellow-700"
                                                : "bg-red-100 text-red-700"
                                        }`}
                                >
                                    {status.status === "good" ? (
                                        <CheckCircle className="w-3 h-3" strokeWidth={2.5} />
                                    ) : (
                                        <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
                                    )}
                                    {status.label}
                                </div>
                            </div>

                            {/* Temperature Display */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div
                                        className={`p-3 rounded-xl ${status.color === "green"
                                                ? "bg-green-50"
                                                : status.color === "yellow"
                                                    ? "bg-yellow-50"
                                                    : status.color === "red"
                                                        ? "bg-red-50"
                                                        : "bg-blue-50"
                                            }`}
                                    >
                                        <Thermometer
                                            className={`w-6 h-6 ${status.color === "green"
                                                    ? "text-green-600"
                                                    : status.color === "yellow"
                                                        ? "text-yellow-600"
                                                        : status.color === "red"
                                                            ? "text-red-600"
                                                            : "text-blue-600"
                                                }`}
                                            strokeWidth={2}
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-3xl font-bold text-gray-900">
                                                {temp.current}
                                            </span>
                                            <span className="text-xl font-semibold text-gray-500">
                                                °C
                                            </span>
                                        </div>
                                        <div
                                            className={`flex items-center gap-1 text-xs font-semibold ${temp.trend === "up"
                                                    ? "text-red-500"
                                                    : "text-blue-500"
                                                }`}
                                        >
                                            <TrendIcon className="w-3 h-3" strokeWidth={2.5} />
                                            {temp.trend === "up" ? "Subiendo" : "Bajando"}
                                        </div>
                                    </div>
                                </div>

                                {/* Mini Chart */}
                                <div className="flex-shrink-0">
                                    <MiniChart data={temp.history} status={status.color} />
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                    <span className="text-xs text-gray-500">
                                        En línea
                                    </span>
                                </div>
                                <span className="text-xs text-gray-400">
                                    Actualizado hace {Math.floor((new Date() - temp.lastUpdate) / 1000)}s
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
                <h4 className="text-sm font-bold text-gray-700 mb-3">
                    Rangos de Temperatura Recomendados
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-xs text-gray-600">
                            <strong>&lt; 0°C</strong> - Muy Frío
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-gray-600">
                            <strong>0-4°C</strong> - Óptimo
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-xs text-gray-600">
                            <strong>4-6°C</strong> - Alerta
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-xs text-gray-600">
                            <strong>&gt; 6°C</strong> - Peligro
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IOTRefri;


